import { Button, Col, Form, Row, Space, theme } from "antd";
import { useEffect, useState } from "react";
import Data from "../controls/Data";
import {
  getCompanyUserRoles,
  getCompanyIndustries,
  getCompanies,
  getTenants,
} from "../repository/data.repository";
import { SearchQuery } from "../code/search-query/search-query";
import {
  companyRolesViewColumns,
  mapCompanyRolesViewQuery,
} from "./RolesView.default";
import { createSearchRequest } from "../hooks/useSearchRequest";
import { createGridRequest } from "../hooks/useGridRequest";
import DynamicForm from "../controls/DynamicForm";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

export interface RoleViewProps {
  readonly trigger?: string;
  readonly onAction?: (key: string, record: any) => void;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
}

function RolesView(props: RoleViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{
    readonly searchQuery: SearchQuery;
  }>({
    searchQuery: {
      conditions: [],
    },
  });
  const [valuesValid, setValuesValid] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  useEffect(() => {
    setContext({ ...context });
  }, [props.trigger]);

  // Events

  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    const searchQuery = mapCompanyRolesViewQuery(values);
    setContext({
      searchQuery,
    });
    props.onSearch?.(searchQuery);
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
    setContext({
      searchQuery: {
        conditions: [],
      },
    });
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  // Methods

  const request = createGridRequest(getCompanyUserRoles, { retry: true });
  const industriesRequest = createSearchRequest(getCompanyIndustries, "name");
  const companiesRequest = createSearchRequest(getCompanies, "name");
  const tenantsRequest = createSearchRequest(getTenants, "name");

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      {/* Form */}
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button disabled={!valuesValid || loading} onClick={onClearData}>
            Clear
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!valuesValid}
            onClick={onSearchData}
          >
            Search
          </Button>
        </Space>
        <DynamicForm
          className="w-72 overflow-y-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                  },
                  {
                    type: "textbox",
                    name: "description",
                    label: "Description",
                  },
                  {
                    type: "togglebox",
                    name: "isManager",
                    label: "Manager",
                  },
                  {
                    type: "combobox",
                    name: "company",
                    label: "Company",
                    request: companiesRequest,
                  },
                  {
                    type: "combobox",
                    name: "domain",
                    label: "Industry",
                    request: industriesRequest,
                  },
                  {
                    type: "combobox",
                    name: "tenant",
                    label: "Tenant",
                    request: tenantsRequest,
                  },
                  {
                    type: "numberbox",
                    name: "usersCount",
                    label: "Users",
                  },
                ],
              },
            ],
          }}
        />
      </Col>
      {/* Data */}
      <Data
        columns={companyRolesViewColumns}
        context={context}
        pageSize={25}
        actions={[
          {
            key: "update",
            label: "Edit",
            disabled: !permissions.can("CompanyRoles", "edit"),
            handler: onAction,
          },
          {
            key: "delete",
            label: "Delete",
            danger: true,
            disabled: !permissions.can("CompanyRoles", "delete"),
            handler: onAction,
          },
        ]}
        request={request}
        rowKey={({ uid }) => uid}
        onLoading={onLoading}
      />
    </Row>
  );
}

export default RolesView;
